import { FC, useState, useEffect, ReactNode, useMemo } from 'react'
import { Drawer, Box } from '@mui/material'

export type MainDrawerProps = {
  trigger?: ReactNode
  children: ReactNode
  openFrom?: 'left' | 'right'
  open?: boolean
  onClose?: () => void
  onOpen?: () => void
}

export const MainDrawer: FC<MainDrawerProps> = ({
  trigger,
  children,
  openFrom = 'right',
  open,
  onClose,
  onOpen,
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false)
  const isControlled = open !== undefined
  const isOpen = isControlled ? open : internalIsOpen

  useEffect(() => {
    if (!isOpen) return undefined
    document.body.style.overflowY = 'hidden'
    return () => {
      document.body.style.overflowY = 'unset'
    }
  }, [isOpen])

  const handleOpen = () => {
    if (isControlled) {
      onOpen?.()
      return
    }
    setInternalIsOpen(true)
    onOpen?.()
  }

  const handleClose = () => {
    if (isControlled) {
      onClose?.()
      return
    }
    setInternalIsOpen(false)
    onClose?.()
  }

  const drawerPropsByOpeningPositions = useMemo(() => {
    if (openFrom === 'left') {
      return {
        borderTopRightRadius: '0.5rem',
        borderBottomRightRadius: '0.5rem',
        overflowX: 'hidden' as const,
      }
    }

    return {
      borderTopLeftRadius: '0.5rem',
      borderBottomLeftRadius: '0.5rem',
      overflowX: 'hidden' as const,
    }
  }, [openFrom])

  return (
    <>
      {trigger ? <Box onClick={handleOpen}>{trigger}</Box> : null}
      <Drawer
        anchor={openFrom}
        open={isOpen}
        ModalProps={{ disableScrollLock: true }}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: '21rem',
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'column',
            height: '100dvh',
            maxHeight: '100dvh',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            pb: 'env(safe-area-inset-bottom)',
            ...drawerPropsByOpeningPositions,
          },
        }}
      >
        {children}
      </Drawer>
    </>
  )
}
